import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthService } from '../services/auth.service';
import { StorageService } from '../services/storage.service';

export const useCredentialValidation = () => {
  const navigate = useNavigate();

  const validateCredentials = useCallback(async (): Promise<boolean> => {
    try {
      // Verificar si hay tokens
      const accessToken = StorageService.getAccessToken();
      const refreshToken = StorageService.getRefreshToken();
      
      if (!accessToken || !refreshToken) {
        console.log('No tokens found, redirecting to login');
        AuthService.logout();
        navigate('/auth/login', { replace: true });
        return false;
      }

      // Verificar si el token está expirado
      const isExpired = StorageService.isTokenExpired();
      if (isExpired) {
        console.log('Token expired, attempting refresh');
        try {
          await AuthService.refreshToken();
          return true;
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          AuthService.logout();
          navigate('/auth/login', { replace: true });
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error('Credential validation failed:', error);
      AuthService.logout();
      navigate('/auth/login', { replace: true });
      return false;
    }
  }, [navigate]);

  const checkAndRedirect = useCallback(async () => {
    const isValid = await validateCredentials();
    if (!isValid) {
      // La redirección ya se maneja en validateCredentials
      return false;
    }
    return true;
  }, [validateCredentials]);

  // Verificar credenciales al montar el componente
  useEffect(() => {
    checkAndRedirect();
  }, [checkAndRedirect]);

  // Verificar credenciales cuando se hace focus en la ventana
  useEffect(() => {
    const handleFocus = () => {
      checkAndRedirect();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [checkAndRedirect]);

  // Verificar credenciales cuando se vuelve a la pestaña
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkAndRedirect();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [checkAndRedirect]);

  return {
    validateCredentials,
    checkAndRedirect,
  };
};
